import React, {
 useState, useEffect
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Loader2
} from 'lucide-react';
import {
 connectionService
} from 'shared/api/services/BEX';

interface SchemaSettingsTabProps {
    entityName: string;
    setEntityName: (v: string) => void;
    connectionId: string;
    setConnectionId: (v: string) => void;
    tableName: string;
    isNewSchema: boolean;
}

interface ConnectionOption {
    id: string;
    name: string;
    type: string;
}

const SchemaSettingsTab: React.FC<SchemaSettingsTabProps> = ({
    entityName, setEntityName, connectionId, setConnectionId, tableName, isNewSchema,
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const [connections, setConnections] = useState<ConnectionOption[]>([]);
    const [isLoadingConnections, setIsLoadingConnections] = useState(true);
    const [description, setDescription] = useState('');

    useEffect(() => {
        const loadConnections = async () => {
            if (!oragID || !appId) return;
            try {
                const data = await connectionService.getConnections(oragID, appId);
                if (Array.isArray(data)) {
                    setConnections(data.map((c: any) => ({
                        id: c.connection_id,
                        name: c.connection_name,
                        type: c.source_type,
                    })));
                }
            } catch (error) {
                console.error('Error loading connections:', error);
            } finally {
                setIsLoadingConnections(false);
            }
        };
        loadConnections();
    }, [oragID, appId]);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">Schema Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 max-w-xl">
                <div className="space-y-2">
                    <Label>Schema Display Name</Label>
                    <Input
                        value={entityName}
                        onChange={(e) => setEntityName(e.target.value)}
                        placeholder="e.g., User Profiles"
                    />
                    <p className="text-xs text-muted-foreground">
                        A human-readable name for this schema.
                    </p>
                </div>

                <div className="space-y-2">
                    <Label>Table / Entity Name</Label>
                    <Input
                        value={tableName}
                        disabled
                        className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                        The technical name used in the database. {isNewSchema ? 'Set in the header above.' : 'Cannot be changed after creation.'}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label>Connection</Label>
                    {isLoadingConnections ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading connections...
                        </div>
                    ) : (
                        <Select value={connectionId} onValueChange={setConnectionId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a connection (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">No connection</SelectItem>
                                {connections.map((conn) => (
                                    <SelectItem key={conn.id} value={conn.id}>
                                        {conn.name} ({conn.type})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <p className="text-xs text-muted-foreground">
                        Link this schema to a data source connection.
                    </p>
                </div>

                <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe the purpose of this schema..."
                        rows={3}
                        className="resize-none"
                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default SchemaSettingsTab;
